import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, CreditCard } from 'lucide-react';
import { Monogram, Wordmark } from './Brand';
import NavDrawer from './NavDrawer';
import { trackEvent } from '../analytics';
import { getScrollBehavior } from '../lib/motion';
import './HeaderLogo.css';

// Header (design-system pass 2026-09):
//   [logo lockup]  [review badge ≥1100]  [nav links ≥1100]  [Request a quote ≥1100]  [☰ <1100]
// Black, 1px hairline bottom border, 84px mobile / 88px desktop. After 40px
// of scroll it condenses to a blurred rgba(10,10,10,0.82) bar. The review
// badge stays right of CONSTRUCTION; on narrower screens the hamburger is
// the only right-side control.
//
// LOGO / HOME LINK — a React Router <Link to="/"> so it works on every
// route (service pages land Home). Do NOT swap for "#top" — that anchor
// only exists on Home (dead link elsewhere). Confirmed 2026-08-01.
const DESKTOP_NAV = [
  { href: '#services',  label: 'Services' },
  { href: '#my-work',   label: 'Gallery' },
  { href: '#catalogue', label: 'Showroom' },
  { to: '/about',       label: 'About' },
  { href: '#reviews',   label: 'Reviews' },
];

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuBtnRef = useRef(null);

  const handleLogoClick = (e) => {
    trackEvent('nav_click', { target: 'home_logo' });
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: getScrollBehavior() });
    }
  };

  // Hash anchors (#services etc.) live on Home only — from any other route,
  // navigate Home first, then scroll once the section has mounted.
  // B02 exception: "#contact" is a QUOTE control. When the current page has
  // its own form (every page with a Footer), scroll to THAT form — never
  // navigate away, which unmounted the page and wiped whatever the visitor
  // had typed (root-reproduced on /handyman-hobart). Global Services /
  // Showroom / Reviews navigation from About etc. is unchanged.
  const handleAnchorClick = (href) => (e) => {
    trackEvent('nav_click', { target: href });
    e.preventDefault();
    const id = href.slice(1);
    const local = document.getElementById(id);
    if (location.pathname === '/' || (id === 'contact' && local)) {
      if (local) local.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
      return;
    }
    navigate(`/${href}`);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
    }, 220);
  };

  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Focus returns to the toggle once the panel closes (a11y).
    setTimeout(() => menuBtnRef.current?.focus(), 30);
  };

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header
        className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`.trim()}
        data-testid="site-header"
      >
        <div className="container site-header-inner">
          <Link
            to="/"
            className="site-brand site-brand--emblem site-brand--animated"
            data-testid="site-brand"
            aria-label="FairFix Construction — home"
            onClick={handleLogoClick}
          >
            <Monogram variant="dark" size={44} title="FairFix" glow />
            <Wordmark variant="dark" className="site-header-wordmark-svg" glow />
            <span className="site-header-constr" aria-hidden="true">CONSTRUCTION</span>
          </Link>

          <nav className="site-header-nav" aria-label="Primary" data-testid="site-header-nav">
            {DESKTOP_NAV.map((item) =>
              item.to ? (
                <Link
                  key={item.label}
                  to={item.to}
                  className="site-header-nav-link"
                  data-testid={`header-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => trackEvent('nav_click', { target: item.to })}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  className="site-header-nav-link"
                  data-testid={`header-nav-${item.label.toLowerCase()}`}
                  onClick={handleAnchorClick(item.href)}
                >
                  {item.label}
                </a>
              )
            )}
            <a
              href="#contact"
              className="header-quote-btn"
              data-testid="header-quote-btn"
              onClick={(e) => {
                trackEvent('quote_cta_click', { location: 'header' });
                handleAnchorClick('#contact')(e);
              }}
            >
              Request a quote
            </a>
          </nav>

          <Link to="/pay" className="header-pay-link" data-testid="header-pay-link" aria-label="Pay an invoice by card"
            onClick={() => trackEvent('nav_click', { target: 'pay_by_card' })}>
            <CreditCard size={19} strokeWidth={1.7} aria-hidden="true" /><span>Pay by card</span>
          </Link>

          <button
            ref={menuBtnRef}
            type="button"
            className="site-header-menu-btn"
            data-testid="header-menu-btn"
            aria-label="Open menu"
            aria-expanded={drawerOpen ? 'true' : 'false'}
            aria-controls="site-nav-drawer"
            onClick={() => {
              trackEvent('menu_open', { location: 'header' });
              setDrawerOpen(true);
            }}
          >
            <Menu size={24} strokeWidth={2.4} aria-hidden="true" />
          </button>
        </div>
      </header>
      <NavDrawer open={drawerOpen} onClose={closeDrawer} />
    </>
  );
};

export default Header;
